import React, { useMemo } from 'react';
import SummarySection from '../components/SummarySection';
import { useLanguage } from '../context/LanguageContext';
import Hero from '../components/sections/Hero';

const coreLanguages = [
  { name: "Rust", role: "High-Concurrency Core & Memory Safety" },
  { name: "PHP (Laravel)", role: "Enterprise APIs & Business Logic" },
  { name: "Go (Golang)", role: "Microservices & Distributed Daemons" },
  { name: "TypeScript", role: "Type-Safe Full-Stack Platforms" },
  { name: "C", role: "Low-Level Systems & Performance" },
  { name: "Python", role: "Automation & Integration Glue" }
];

function Home(props) {
  const { lang, t } = useLanguage();

  const proofs = useMemo(() => [
    {
      badge: "PCI DSS v4.0.1",
      title: lang === 'ar' ? 'امتثال مزود خدمة المدفوعات' : 'Service Provider Compliant',
      subtitle: "Payd Technologies FZE"
    },
    {
      badge: "ISO 27001:2022",
      title: lang === 'ar' ? 'إدارة أمن المعلومات' : 'Certified Information Security',
      subtitle: lang === 'ar' ? 'معتمد على مستوى المجموعة' : 'Group-Wide Certified'
    },
    {
      badge: "Trax Platform",
      title: lang === 'ar' ? 'مراقبة موحدة بلغة Rust' : 'Rust Observability Engine',
      subtitle: lang === 'ar' ? 'المنصة الرائدة مطورة ذاتياً' : 'Flagship Self-Built Platform'
    },
    {
      badge: "Crisis DR Tested",
      title: lang === 'ar' ? 'ترحيل فوري بصفر فقدان للبيانات' : 'Zero-Downtime Crisis Migration',
      subtitle: lang === 'ar' ? 'استجابة لحادثة سحابة AWS' : 'AWS UAE Incident Response'
    }
  ], [lang]);

  const strategicFocus = useMemo(() => [
    {
      icon: (
        <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: lang === 'ar' ? 'بوابات الدفع والمعاملات فائقة السرعة' : 'High-Concurrency Payment Gateways',
      desc: lang === 'ar'
        ? 'بناء محركات معالجة مالية في أجزاء من الثانية بلغة Rust و Laravel متوافقة مع متطلبات PCI DSS v4.0.1.'
        : 'Sub-second transaction processing cores engineered in Rust & Laravel under full-scope PCI DSS v4.0.1 compliance.'
    },
    {
      icon: (
        <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: lang === 'ar' ? 'إدارة الأزمات والتعافي السريع (DR)' : 'Crisis Management & Zero-Downtime DR',
      desc: lang === 'ar'
        ? 'خطط استمرارية أعمال مجربة عملياً مع ترحيل سحابي فوري دون أي فقدان للبيانات خلال انقطاعات السحابة الإقليمية.'
        : 'Battle-tested business continuity runbooks and emergency zero-loss cloud migrations during regional infrastructure outages.'
    },
    {
      icon: (
        <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      title: lang === 'ar' ? 'حماية محيط الشبكات وجدران الحماية' : 'Perimeter Defense & Multi-Layer Firewalls',
      desc: lang === 'ar'
        ? 'نشر منظومات CrowdSec و Suricata و OPNsense/pfSense وجدران حماية الويب AWS/Azure WAF مع قواعد OWASP CRS.'
        : 'Deploying CrowdSec, Suricata IDS/IPS, OPNsense/pfSense, and cloud WAFs (AWS/Azure) with OWASP CRS protection.'
    },
    {
      icon: (
        <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      title: lang === 'ar' ? 'قيادة مركز الموارد المشتركة للمجموعة' : 'Shared Resource Hub & Executive Leadership',
      desc: lang === 'ar'
        ? 'إدارة الفرق التقنية، هندسة الحلول، والحوكمة الأمنية عبر شركات المجموعة (Click2Secure, Payd, Starz TPA, Seguro, Trustcare).'
        : 'Directing technology, solutions architecture, and security governance across group entities in fintech and healthtech.'
    }
  ], [lang]);

  const focusList = useMemo(() => {
    const list = t('sections.focusList');
    if (Array.isArray(list)) return list;
    return [
      {
        title: t('sections.focus1'),
        desc: lang === 'ar'
          ? 'تصميم بنى تحتية فائقة الموثوقية وخطط تعافٍ مجربة تضمن استمرارية الأعمال وصفر فقدان في البيانات تحت أعلى درجات الضغط التشغيلي.'
          : 'Architecting fault-tolerant infrastructure and battle-tested disaster recovery runbooks that guarantee business continuity and zero data loss under peak load.',
        tag: 'Resilience'
      },
      {
        title: t('sections.focus2'),
        desc: lang === 'ar'
          ? 'بناء بوابات معالجة مالية متقدمة بلغة Rust وواجهات برمجية بلغة Laravel و Go، مدعومة باستراتيجيات تخزين مؤقت وفهرسة متقدمة.'
          : 'Engineering sub-second transaction gateways in Rust and high-efficiency APIs in Laravel & Go, backed by optimized caching and database indexing.',
        tag: 'Performance'
      },
      {
        title: t('sections.focus3'),
        desc: lang === 'ar'
          ? 'دمج ضوابط الأمان الشاملة (PCI DSS v4.0.1، ISO 27001، ADHICS، WAF، IDS/IPS) مباشرة في شيفرات البرمجة وطوبولوجيا الخوادم.'
          : 'Embedding end-to-end security controls (PCI DSS v4.0.1, ISO 27001, ADHICS, WAF, IDS/IPS) directly into code pipelines and infrastructure topologies.',
        tag: 'Compliance'
      }
    ];
  }, [t, lang]);

  const interestedList = useMemo(() => {
    const list = t('sections.interestedList');
    return Array.isArray(list) ? list : [];
  }, [t]);

  return (
    <div className="space-y-20 animate-fade-in text-white max-w-6xl mx-auto">
      {/* Hero Section */}
      <Hero />

      {/* Executive Proof & Milestone Ribbon */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {proofs.map((item, idx) => (
          <div
            key={idx}
            className="p-5 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-primary-500/40 hover:bg-white/[0.05] transition-all space-y-1.5 shadow-lg group"
          >
            <div className="flex items-center justify-between">
              <span className="px-2.5 py-0.5 rounded-md text-[11px] font-extrabold uppercase tracking-wider bg-primary-500/15 text-primary-300 border border-primary-500/25">
                {item.badge}
              </span>
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            </div>
            <h4 className="text-sm font-bold text-white group-hover:text-primary-300 transition-colors pt-1">
              {item.title}
            </h4>
            <p className="text-xs text-white/60">
              {item.subtitle}
            </p>
          </div>
        ))}
      </section>

      {/* Strategic Architecture Focus Grid */}
      <section className="space-y-8">
        <div className="space-y-2">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-400">
            {lang === 'ar' ? 'القدرات الهندسية الأساسية' : 'Core Engineering Pillars'}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight">
            {lang === 'ar' ? 'التركيز المعماري والحلول الحيوية' : 'Strategic Architecture & Systems Focus'}
          </h2>
          <p className="text-sm sm:text-base text-white/70 max-w-2xl leading-relaxed">
            {lang === 'ar'
              ? 'بناء أنظمة برمجية عالية الموثوقية تجمع بين الأداء الفائق والامتثال التنظيمي والأمان الصارم.'
              : 'Engineering high-reliability systems uniting ultra-low latency, strict regulatory compliance, and hardened security.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {strategicFocus.map((item, idx) => (
            <div
              key={idx}
              className="p-7 rounded-3xl bg-white/[0.03] border border-white/10 hover:border-primary-500/40 hover:bg-white/[0.05] transition-all space-y-3 group shadow-lg"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  {item.icon}
                </div>
                <h3 className="text-base sm:text-lg font-bold text-white group-hover:text-primary-300 transition-colors">
                  {item.title}
                </h3>
              </div>
              <p className="text-sm text-white/75 leading-relaxed font-normal">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Redesigned "I Focus On" & "Interested In" Section */}
      <section className="space-y-8">
        <div className="space-y-2">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-400">
            {lang === 'ar' ? 'التوجّه التقني والاهتمامات' : 'Engineering Focus & Technical Interests'}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white tracking-tight">
            {lang === 'ar' ? 'مجالات التركيز والاهتمامات الهندسية' : 'Core Priorities & Engineering Exploration'}
          </h2>
          <p className="text-sm sm:text-base text-white/70 max-w-2xl leading-relaxed">
            {lang === 'ar'
              ? 'مبادئ معمارية تحكم خياراتي الهندسية اليومية، إلى جانب التقنيات والأنظمة الناشئة التي أعمل على تطويرها واستكشافها باستمرار.'
              : 'Architectural principles guiding my daily execution, alongside emerging technologies and systems I actively explore and build for.'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          {/* Left Column (7 cols): I Focus On */}
          <div className="lg:col-span-7 p-7 sm:p-9 rounded-[2.5rem] bg-gradient-to-br from-white/[0.04] to-white/[0.015] border border-white/10 shadow-xl space-y-6 flex flex-col justify-between">
            <div className="space-y-5">
              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 rounded-2xl bg-primary-500/10 border border-primary-500/25 flex items-center justify-center shrink-0 shadow-inner">
                  <svg className="w-6 h-6 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                    {t('sections.focusOnTitle')}
                  </h3>
                  <p className="text-xs sm:text-sm text-white/60">
                    {t('sections.focusSubtitle')}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 pt-1">
                {focusList.map((item, idx) => (
                  <div
                    key={idx}
                    className="p-4 sm:p-5 rounded-2xl bg-white/[0.025] hover:bg-white/[0.05] border border-white/5 hover:border-primary-500/30 transition-all space-y-1.5 group"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <h4 className="text-sm sm:text-base font-bold text-white group-hover:text-primary-300 transition-colors flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-primary-400 shrink-0"></span>
                        {item.title}
                      </h4>
                      {item.tag && (
                        <span className="text-[10px] uppercase font-extrabold tracking-wider px-2 py-0.5 rounded-md bg-white/5 text-primary-300 border border-primary-500/20 shrink-0">
                          {item.tag}
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-white/70 leading-relaxed font-normal">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-white/10 flex items-center gap-3 text-xs text-white/60">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0"></span>
              <span>
                {lang === 'ar'
                  ? 'تركيز عملي مثبت في بيئات الإنتاج الفعلية وأثناء الأزمات.'
                  : 'Battle-tested in real production environments and under crisis load.'}
              </span>
            </div>
          </div>

          {/* Right Column (5 cols): Interested In & Favorite Stack */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="p-7 sm:p-8 rounded-[2.5rem] bg-gradient-to-br from-white/[0.04] to-white/[0.015] border border-white/10 shadow-xl space-y-6 flex-1 flex flex-col justify-between">
              <div className="space-y-5">
                <div className="flex items-center gap-3.5">
                  <div className="w-11 h-11 rounded-2xl bg-secondary-500/10 border border-secondary-500/25 flex items-center justify-center shrink-0 shadow-inner">
                    <svg className="w-6 h-6 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                      {t('sections.interestedIn')}
                    </h3>
                    <p className="text-xs sm:text-sm text-white/60">
                      {t('sections.interestedSubtitle')}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2.5 pt-1">
                  {interestedList.map((item, idx) => (
                    <span
                      key={idx}
                      className="px-3.5 py-2 rounded-xl text-xs font-medium bg-white/[0.03] hover:bg-white/[0.08] text-white/80 hover:text-white border border-white/10 hover:border-primary-500/40 transition-all flex items-center gap-2 group cursor-default"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400/60 group-hover:bg-primary-400 transition-colors"></span>
                      <span>{item}</span>
                    </span>
                  ))}
                </div>
              </div>

              {/* Languages Quick Bar */}
              <div className="pt-5 border-t border-white/10 space-y-2.5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-white/40 block">
                  {t('sections.favoriteLanguages')}
                </span>
                <div className="flex flex-wrap gap-2">
                  {["Rust 🦀", "PHP 🐘", "Go 🦫", "TypeScript", "C", "Python 🐍"].map((langChip, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-white/[0.04] text-white/90 border border-white/10 hover:border-primary-500/30 transition-colors"
                    >
                      {langChip}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Languages & Systems Runtimes in Practice */}
      <section className="p-8 sm:p-10 rounded-[2.5rem] bg-gradient-to-br from-white/[0.04] to-white/[0.015] border border-white/10 shadow-xl space-y-6">
        <div className="space-y-1">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-400">
            {t('sections.favoriteLanguages')}
          </span>
          <h3 className="text-xl sm:text-2xl font-bold text-white">
            {lang === 'ar' ? 'لغات الأنظمة والتقنيات الأساسية' : 'Core Languages & Runtimes in Practice'}
          </h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 pt-2">
          {coreLanguages.map((langItem, idx) => (
            <div
              key={idx}
              className="p-4 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-primary-500/40 hover:bg-white/[0.06] transition-all flex items-center gap-3 group"
            >
              <span className="w-2 h-2 rounded-full bg-primary-400 shrink-0"></span>
              <div>
                <span className="font-bold text-sm text-white group-hover:text-primary-300 transition-colors block">
                  {langItem.name}
                </span>
                <span className="text-xs text-white/60">
                  {langItem.role}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Summary Section (Story Pillars Preview, Experience, Education, CV Download) */}
      <SummarySection />
    </div>
  );
}

export default Home;
